import { useMemo } from "react";
import {
  Alert,
  Badge,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCode,
  IconLayoutGrid,
  IconTemplate,
  IconUsersGroup,
} from "@tabler/icons-react";
import {
  LuaBlock,
  PresetCallout,
  useStepRuleSet,
} from "./components/SharedRuleParts";
import {
  GameTemplateView,
  GroupTemplateView,
  RoundTemplateView,
} from "./types";

const STRUCTURE_SKELETON_HEIGHTS = [28, 56, 220, 220] as const;

const GameTemplateCard = ({ game }: { game: GameTemplateView }) => {
  const hasOnWin =
    game.onWinTarget !== null ||
    game.onWinSourceSlot !== null ||
    game.onWinTargetSlot !== null;
  const hasOnLoss =
    game.onLossTarget !== null ||
    game.onLossSourceSlot !== null ||
    game.onLossTargetSlot !== null;

  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Badge variant="filled" color="gray" size="xs">
              Game #{game.order + 1}
            </Badge>
            <Badge variant="light" color="blue" size="xs">
              {game.teamSlots} slots
            </Badge>
            <Badge variant="light" color="grape" size="xs">
              Bo{game.bestOf}
            </Badge>
          </Group>
        </Group>

        {hasOnWin && (
          <Stack gap={2}>
            <Group gap={6}>
              <Text size="xs" c="dimmed" fw={600}>
                On Win
              </Text>
              <IconArrowRight size={12} />
              {game.onWinSourceSlot != null && (
                <Badge size="xs" variant="outline">
                  slot #{game.onWinSourceSlot}
                </Badge>
              )}
              {game.onWinTargetSlot != null && (
                <Badge size="xs" variant="outline" color="teal">
                  → slot #{game.onWinTargetSlot}
                </Badge>
              )}
            </Group>
            {game.onWinTarget && (
              <LuaBlock
                label="Target expression"
                value={game.onWinTarget}
                maxHeight={140}
              />
            )}
          </Stack>
        )}

        {hasOnLoss && (
          <Stack gap={2}>
            <Group gap={6}>
              <Text size="xs" c="dimmed" fw={600}>
                On Loss
              </Text>
              <IconArrowRight size={12} />
              {game.onLossSourceSlot != null && (
                <Badge size="xs" variant="outline">
                  slot #{game.onLossSourceSlot}
                </Badge>
              )}
              {game.onLossTargetSlot != null && (
                <Badge size="xs" variant="outline" color="orange">
                  → slot #{game.onLossTargetSlot}
                </Badge>
              )}
            </Group>
            {game.onLossTarget && (
              <LuaBlock
                label="Target expression"
                value={game.onLossTarget}
                maxHeight={140}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

const RoundTemplateCard = ({ round }: { round: RoundTemplateView }) => {
  const sortedGames = useMemo(
    () => [...round.gameTemplates].sort((a, b) => a.order - b.order),
    [round.gameTemplates],
  );

  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap="sm">
        <Group gap="xs">
          <Badge variant="filled" color="gray" size="sm">
            Round #{round.order + 1}
          </Badge>
          <Text fw={600}>{round.name}</Text>
          <Badge variant="light" size="xs">
            {round.gameTemplates.length} game(s)
          </Badge>
        </Group>
        {sortedGames.length === 0 ? (
          <Text size="xs" c="dimmed" fs="italic">
            No games configured
          </Text>
        ) : (
          <Stack gap="xs">
            {sortedGames.map((game) => (
              <GameTemplateCard key={game.id} game={game} />
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

const GroupTemplateCard = ({ group }: { group: GroupTemplateView }) => {
  const isDynamic = !!group.generationScript;
  const sortedRounds = useMemo(
    () => [...group.roundTemplates].sort((a, b) => a.order - b.order),
    [group.roundTemplates],
  );

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconUsersGroup size={18} />
            <Text fw={600}>{group.name}</Text>
            <Badge variant="filled" color="gray" size="sm">
              #{group.order + 1}
            </Badge>
          </Group>
          <Badge
            variant="light"
            color={isDynamic ? "indigo" : "teal"}
            leftSection={
              isDynamic ? <IconCode size={12} /> : <IconLayoutGrid size={12} />
            }
          >
            {isDynamic ? "Mode 1: Dynamic Generation" : "Mode 2: Static"}
          </Badge>
        </Group>

        {group.description && (
          <Text size="sm" c="dimmed">
            {group.description}
          </Text>
        )}

        <Group gap="md">
          <LuaBlock
            label="Team slots"
            value={group.teamSlotsExpr}
            maxHeight={120}
          />
          <LuaBlock label="Best of" value={group.bestOfExpr} maxHeight={120} />
          <LuaBlock
            label="Team slots per game"
            value={group.teamSlotsPerGameExpr}
            maxHeight={120}
          />
          {group.roundCountExpr != null && (
            <LuaBlock
              label="Round count"
              value={group.roundCountExpr}
              maxHeight={120}
            />
          )}
        </Group>

        <Divider />

        {isDynamic ? (
          <LuaBlock
            label="Generation script"
            value={group.generationScript}
            maxHeight={360}
          />
        ) : (
          <Stack gap="sm">
            <Group gap="xs">
              <Text fw={600}>Round templates</Text>
              <Badge variant="light" size="xs">
                {group.roundTemplates.length}
              </Badge>
            </Group>
            {sortedRounds.length === 0 ? (
              <Text size="xs" c="dimmed" fs="italic">
                No round templates configured
              </Text>
            ) : (
              <Stack gap="sm">
                {sortedRounds.map((round) => (
                  <RoundTemplateCard key={round.id} round={round} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export const StructureEditor = () => {
  const { ruleSet, fallback } = useStepRuleSet({
    title: "Structure Template",
    icon: <IconTemplate size={20} />,
    skeletonHeights: STRUCTURE_SKELETON_HEIGHTS,
  });

  const structureTemplate = ruleSet?.structureTemplate ?? null;

  const groups = useMemo(
    () =>
      [...(structureTemplate?.groupTemplates ?? [])].sort(
        (a, b) => a.order - b.order,
      ),
    [structureTemplate?.groupTemplates],
  );

  if (!ruleSet) return fallback;

  if (!structureTemplate) {
    return (
      <Stack>
        <Group gap="xs">
          <IconTemplate size={20} />
          <Title order={2}>Structure Template</Title>
        </Group>
        <Alert
          color="yellow"
          icon={<IconAlertCircle size={16} />}
          variant="light"
        >
          This rule set has no structure template configured. Apply a preset to
          configure the structure template.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack>
      <Group gap="xs">
        <IconTemplate size={20} />
        <Title order={2}>Structure Template</Title>
        <Badge variant="light" size="lg">
          {groups.length} group(s)
        </Badge>
      </Group>

      <PresetCallout />

      {groups.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No groups configured
        </Text>
      ) : (
        <Stack gap="md">
          {groups.map((group) => (
            <GroupTemplateCard key={group.id} group={group} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
